import { useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Check, ArrowDownUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { api, errMsg, fmt, curText } from "@/lib/api";
import { CoinIcon } from "@/components/Brand";

const ROWS = [["gc", "Gold Coins"], ["sc", "Sweeps Coins"], ["usdc", "USDC Balance"]];
const show = (cur, v) => (cur === "usdc" ? `$${fmt(v, cur)}` : fmt(v, cur));

function ConvertPanel() {
  const { user, setUser } = useApp();
  const [dir, setDir] = useState("sc_to_usdc");
  const [amt, setAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const from = dir === "sc_to_usdc" ? "sc" : "usdc";
  const max = from === "sc" ? user?.sc_redeemable ?? 0 : user?.usdc ?? 0;
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/wallet/convert", { direction: dir, amount: Number(amt) });
      setUser(data); setAmt("");
      toast.success(dir === "sc_to_usdc" ? `Converted ${Number(amt).toFixed(2)} SC to USDC` : `Converted $${Number(amt).toFixed(2)} USDC to SC`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 rounded-xl bg-[#0B0E14] p-3" data-testid="convert-panel">
      <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wide text-gray-400">
        <span>Convert {dir === "sc_to_usdc" ? "SC → USDC" : "USDC → SC"}</span>
        <button data-testid="convert-flip-btn" onClick={() => setDir((d) => (d === "sc_to_usdc" ? "usdc_to_sc" : "sc_to_usdc"))} className="p-1 rounded-md hover:bg-white/10 transition-colors"><ArrowDownUp size={14} /></button>
      </div>
      <div className="mt-2 flex items-center gap-2 rounded-lg bg-[#151A22] border border-white/10 px-2">
        <CoinIcon cur={from} size={18} />
        <input data-testid="convert-amount-input" type="number" inputMode="decimal" placeholder="0.00" value={amt} onChange={(e) => setAmt(e.target.value)} className="flex-1 w-0 bg-transparent py-2 text-sm font-bold outline-none" />
        <button data-testid="convert-max-btn" onClick={() => setAmt(String(Math.floor(max * 100) / 100))} className="text-[10px] font-black text-gray-400 hover:text-white">MAX</button>
      </div>
      <div className="mt-1 text-[10px] text-gray-500">1 SC = $1.00 USDC · {from === "sc" ? "redeemable SC only" : "available"}: {show(from, max)}</div>
      <button data-testid="convert-submit-btn" disabled={busy || !(Number(amt) > 0)} onClick={go} className="mt-2 w-full flex items-center justify-center gap-1 rounded-lg bg-[#2775CA] hover:bg-[#2f86e6] disabled:opacity-40 py-2 text-xs font-black transition-[background-color,opacity]">
        {busy && <Loader2 size={12} className="animate-spin" />}CONVERT
      </button>
    </div>
  );
}

export function BalanceDropdown() {
  const { user, currency, setCurrency } = useApp();
  const [open, setOpen] = useState(false);
  const active = ROWS.find((r) => r[0] === currency) || ROWS[0];
  return (
    <div className="relative" data-testid="balance-dropdown">
      <button onClick={() => setOpen((o) => !o)} data-testid="balance-trigger" className="flex items-center gap-2 rounded-full bg-[#151A22] border border-white/10 pl-2 pr-3 py-1.5 hover:border-white/20 transition-colors">
        <CoinIcon cur={active[0]} size={20} />
        <span className={`font-bold text-sm ${curText(active[0])}`} data-testid="balance-active">{show(active[0], user?.[active[0]] ?? 0)}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          {createPortal(<div className="fixed inset-0 z-[39]" data-testid="balance-backdrop" onClick={() => setOpen(false)} />, document.body)}
          <div className="absolute left-0 mt-2 w-64 z-50 rounded-2xl bg-[#151A22] border border-white/10 shadow-2xl p-2" data-testid="balance-menu">
            {ROWS.map(([k, label]) => (
              <button key={k} data-testid={`balance-opt-${k}`} onClick={() => { setCurrency(k); setOpen(false); }} className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${currency === k ? "bg-white/5" : "hover:bg-white/5"}`}>
                <CoinIcon cur={k} size={26} />
                <span className="flex-1 text-left"><span className="block text-xs text-gray-400">{label}</span><span className={`font-bold ${curText(k)}`}>{show(k, user?.[k] ?? 0)}</span></span>
                {currency === k && <Check size={16} className="gc-text" />}
              </button>
            ))}
            <ConvertPanel />
          </div>
        </>
      )}
    </div>
  );
}
